import { MutationFunctionOptions, useLazyQuery } from '@apollo/client'
import {
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { GenerateSharedAccessSignatureQuery } from '@deltek/specification-client/core/GraphQL/Queries/generateSharedAccessSignature.queries'
import {
    GenerateSharedAccessSignatureInput,
    GenerateSharedAccessSignatureResponse,
} from '@deltek/specification-client/core/Models/generateSharedAccessSignature'
import { PickerResponse } from '@deltek/specification-client/core/Widgets/Filestack/FilestackButton'
import { ProductListingDetailConsolidatedProductMedia as ConsolidatedProductMedia } from '@deltek/specification-client/core/Widgets/ProductListing/Details/Media/productListingDetailConsolidatedProductMedia'
import Skeleton from '@material-ui/lab/Skeleton'
import { processEnv } from 'components/configuration/environmentVariables'
import React, { useEffect, useState } from 'react'
import { v4 as newGuid } from 'uuid'
import TabResultArea from './TabResultArea'
import UploadResultItem, { UploadResultItemProps } from './UploadResultItem'
import UploadResultItemLoading from './UploadResultItemLoading'

export const photosFilestackDivId = 'photosFilestackContainer'

export interface PhotosTabContentProps {
    filestackActive: boolean
    filestackLoading: boolean
    filestackResponse?: PickerResponse
    filestackResponseLoading: boolean
    newImagesUploadCount: number
    onUpdate: (
        newProductImages: Record<string, ConsolidatedProductMedia>
    ) => void
    setFilestackResponse: (pickerResponse: PickerResponse | undefined) => void
    setHasUploadResultItems: (hasItems: boolean) => void
    showGlobalSnackbar: (
        options?: MutationFunctionOptions<
            ShowGlobalSnackbarRequest,
            ShowGlobalSnackbarMutationProps
        >
    ) => void
    totalMediaDialogConsolidatedProductMedia: number
    uploadLoading: boolean
    setUploadLoading: (value: boolean) => void
    activeTab?: boolean
}

export const PhotosTabContent = ({
    filestackActive,
    filestackLoading,
    filestackResponse,
    filestackResponseLoading,
    newImagesUploadCount,
    onUpdate,
    setFilestackResponse,
    setHasUploadResultItems,
    showGlobalSnackbar,
    totalMediaDialogConsolidatedProductMedia,
    uploadLoading,
    setUploadLoading,
    activeTab,
}: PhotosTabContentProps) => {
    const [imageItems, setImageItems] = useState<UploadResultItemProps[]>([])
    const [currentSasData, setCurrentSasData] = useState<
        GenerateSharedAccessSignatureResponse | undefined
    >()

    const [
        generateSharedAccessSignature,
        { loading: sasLoading, error: sasError, data: sasData },
    ] = useLazyQuery<
        GenerateSharedAccessSignatureResponse,
        GenerateSharedAccessSignatureInput
    >(GenerateSharedAccessSignatureQuery, {
        fetchPolicy: 'no-cache',
        onCompleted: () => setUploadLoading(false),
    }) // apparently, I still have to add no-cache here so it won't grab old sas

    useEffect(() => {
        if (!!filestackResponse) {
            generateSharedAccessSignature({
                variables: {
                    input: {
                        durationInMinutes: 480, // 8 hours (A day's work)
                        storageAccountName:
                            processEnv.REACT_APP_BlobStorageAccountName,
                    },
                },
            })
        }
    }, [filestackResponse, generateSharedAccessSignature])

    useEffect(() => {
        if (sasData) {
            setCurrentSasData(sasData)
        }
    }, [sasData])

    // Updates the upload item results with correct preview image
    useEffect(() => {
        if (currentSasData && !!filestackResponse) {
            const newProductImages: Record<
                string,
                ConsolidatedProductMedia
            > = {}
            let successfulImageItems: UploadResultItemProps[] = []
            filestackResponse.filesUploaded.forEach((uploaded, idx) => {
                const newOrder = totalMediaDialogConsolidatedProductMedia + idx
                const newImageId = `image-${newGuid()}`
                const imageUrl = `${processEnv.REACT_APP_BlobStorageUrl}/${
                    processEnv.REACT_APP_BlobStorageUnverifiedContainerName
                }/${encodeURIComponent(uploaded.key!)}${
                    currentSasData.generateSharedAccessSignature
                        .sharedAccessSignature
                }`
                newProductImages[newImageId] = {
                    imgUrl: imageUrl,
                    originalFilename: uploaded.filename,
                    order: newOrder,
                    thumbnailUrl: imageUrl,
                    filename: uploaded.key,
                }

                successfulImageItems.push({
                    filename: uploaded.filename,
                    imageUrl,
                    success: true,
                } as UploadResultItemProps)
            })
            const unsuccessfulImageItems = filestackResponse.filesFailed.map(
                (uploaded) => ({
                    filename: uploaded.filename,
                    imageUrl: '',
                    success: false,
                })
            )
            setImageItems([...successfulImageItems, ...unsuccessfulImageItems])
            onUpdate(newProductImages)
            setCurrentSasData(undefined)
            setFilestackResponse(undefined)
            setHasUploadResultItems(true)
        }
    }, [
        currentSasData,
        filestackResponse,
        onUpdate,
        setFilestackResponse,
        setHasUploadResultItems,
        totalMediaDialogConsolidatedProductMedia,
    ])

    useEffect(() => {
        if (!showGlobalSnackbar) return
        if (sasError) {
            showGlobalSnackbar({
                variables: {
                    id: 'PhotosTabContentSaSError',
                    variant: 'error',
                    message: 'Error getting shared access signature.',
                },
            })
        }
    }, [sasError, showGlobalSnackbar])

    // clear images
    useEffect(() => {
        if (filestackActive) {
            setImageItems([])
            setHasUploadResultItems(false)
        }
    }, [filestackActive, setHasUploadResultItems])

    // upload loading
    useEffect(() => {
        if (filestackResponseLoading && activeTab) {
            setUploadLoading(true)
        }
    }, [filestackResponseLoading, setUploadLoading, activeTab])

    return (
        <>
            {filestackLoading && (
                <Skeleton
                    data-testid="filestackLoadingSkeleton"
                    height={500}
                    style={{ backgroundColor: '#eee' }}
                    variant="rect"
                />
            )}
            <div
                id={photosFilestackDivId}
                style={{ height: 500 }}
                hidden={
                    filestackLoading ||
                    sasLoading ||
                    filestackResponseLoading ||
                    !filestackActive
                }
            />
            <TabResultArea
                hidden={filestackLoading || filestackActive}
                title={`${totalMediaDialogConsolidatedProductMedia} of 10 photos/videos added`}
            >
                {uploadLoading &&
                    Array.from({
                        length: newImagesUploadCount,
                    }).map((_c, idx) => <UploadResultItemLoading key={idx} />)}
                {!uploadLoading &&
                    imageItems.length > 0 &&
                    imageItems.map((imageItem, key) => (
                        <UploadResultItem key={key} {...imageItem} />
                    ))}
            </TabResultArea>
        </>
    )
}

export default PhotosTabContent
